/**
 * QA Panel — Sentry Debug controller.
 *
 * Surfaces the data the various tracker singletons are staging for the
 * next Sentry transaction so QA can verify what will get attached on
 * session end before it gets sent. Restores the inspection capability
 * that the deleted ClickTrackingDebugWidget used to provide, plus three
 * new sections (section-navigation tracker, search queries, active span
 * trace id) that didn't exist when the widget was last live.
 *
 * The trackers are imperative singletons that don't expose change events,
 * so this controller polls every 2s to match the cadence of the deleted
 * widget. Reads are wrapped in try/catch — the trackers can transiently
 * throw during teardown / language switches, and the panel must never
 * propagate a render error.
 */

import { eventTracker } from '../../../core/services/EventTracker';
import { transactionTracker } from '../../../core/services/TransactionTracker';
import { userDataTracker } from '../../../core/services/UserDataTracker';
import { sessionService } from '../../../core/services/SessionService';
import { sectionNavigationTracker } from '../../../core/services/SectionNavigationTracker';
import { dynamicSectionsTracker } from '../../../core/services/DynamicSectionsTracker';

/* ── Public state shape sent to the popup ───────────────────────── */

/** One parsed `click_game_…` interaction row, broken out for display. */
export interface SentryDebugGameLaunch {
  /** The original cleanKey (after the Sentry prefix is stripped). */
  key: string;
  /** 'MULTI' | 'SINGLE' | 'UNKNOWN' — derived from the key suffix. */
  viewMode: string;
  section: string;
  game: string;
  count: number;
}

export interface SentryDebugState {
  /** Last 100 raw event keys in the order they fired (newest last). */
  eventOrder: string[];
  /** Navigation-tab clicks, keyed by tab name (Sentry prefix stripped). */
  navTabs: Record<string, number>;
  /** Jackpot statistics drawer opens, keyed by jackpot key (prefix stripped). */
  jackpotStatsOpens: Record<string, number>;
  /** Game-launch clicks, parsed into viewMode / section / game. */
  gameLaunches: SentryDebugGameLaunch[];
  /** All other interaction counters that didn't match a known prefix. */
  otherClicks: Record<string, number>;
  /** Total interactions across every event key. */
  total: number;
  /** UserDataTracker's formatted snapshot. */
  userData: Record<string, string | number>;
  /** TransactionTracker's formatted snapshot (game open data). */
  transaction: Record<string, string | number>;
  /** Section-navigation tracker counters + history. NEW since the widget. */
  sectionNavigation: Record<string, string | number>;
  /** DynamicSectionsTracker's formatted snapshot. NEW. */
  dynamicSections: Record<string, string | number>;
  /** Ordered list of search queries the user has typed. NEW since the widget. */
  searchQueries: string[];
  /** Active Sentry span trace id, or null if no span is running / readable. NEW. */
  activeSpanTraceId: string | null;
  /** Wall-clock timestamp of the snapshot — for the "last refresh" footer. */
  lastUpdate: number;
}

/* ── Parsing helpers (ported from the deleted widget) ──────────── */

const INTERACTIONS_PREFIX = 'egt.lobby.ui.interactions.';
const CLICKS_PREFIX = 'egt.lobby.ui.clicks.';
const INTERACTIONS_ORDER_KEY = 'egt.lobby.ui.interactions.interactions_order';

interface ParsedClickData {
  navTabs: Record<string, number>;
  jackpotStatsOpens: Record<string, number>;
  gameLaunches: SentryDebugGameLaunch[];
  otherClicks: Record<string, number>;
  total: number;
}

function parseClickData(formatted: Record<string, string | number | boolean | string[]>): ParsedClickData {
  const navTabs: Record<string, number> = {};
  const jackpotStatsOpens: Record<string, number> = {};
  const gameLaunches: SentryDebugGameLaunch[] = [];
  const otherClicks: Record<string, number> = {};
  let total = 0;

  Object.entries(formatted).forEach(([key, value]) => {
    // The order list is broadcast separately, skip it here.
    if (key === INTERACTIONS_ORDER_KEY) return;

    const cleanKey = key.replace(INTERACTIONS_PREFIX, '').replace(CLICKS_PREFIX, '');

    if (cleanKey === 'total') {
      if (typeof value === 'number') total = value;
      return;
    }
    if (cleanKey === 'event_order' || cleanKey === 'interactions_order') return;

    // The display contract is "counts only" — array values (e.g. metadata
    // arrays) shouldn't land in any of the count buckets.
    if (typeof value !== 'number') return;

    if (cleanKey.startsWith('click_navigation_')) {
      navTabs[cleanKey.replace('click_navigation_', '')] = value;
      return;
    }
    // Legacy fallback so older sessions still bucket correctly.
    if (cleanKey.startsWith('nav_tab_')) {
      navTabs[cleanKey.replace('nav_tab_', '')] = value;
      return;
    }
    if (cleanKey.startsWith('jackpot_stats_open_')) {
      jackpotStatsOpens[cleanKey.replace('jackpot_stats_open_', '')] = value;
      return;
    }
    if (cleanKey.startsWith('click_game_')) {
      const gameClickPart = cleanKey.replace('click_game_', '');
      const parts = gameClickPart.split('_');
      if (parts.length >= 3) {
        const gameKey = parts[0];
        const viewMode = parts[1];
        const section = parts.slice(2).join('_');
        gameLaunches.push({
          key: cleanKey,
          viewMode: viewMode.toUpperCase(),
          section: section.toUpperCase(),
          game: gameKey,
          count: value,
        });
      } else {
        gameLaunches.push({
          key: cleanKey,
          viewMode: 'UNKNOWN',
          section: 'UNKNOWN',
          game: gameClickPart,
          count: value,
        });
      }
      return;
    }

    otherClicks[cleanKey] = value;
  });

  return { navTabs, jackpotStatsOpens, gameLaunches, otherClicks, total };
}

/** Read the active span's trace id without throwing if Sentry's API drifts. */
function readActiveSpanTraceId(): string | null {
  try {
    const span = sessionService.getActiveSpan();
    if (!span) return null;
    // `spanContext()` is the documented way to read a span's trace id
    // across Sentry SDK versions; wrap the call defensively in case the
    // span shape changes again.
    const ctx = (span as { spanContext?: () => { traceId?: string } }).spanContext?.();
    return ctx?.traceId ?? null;
  } catch {
    return null;
  }
}

/* ── Controller ─────────────────────────────────────────────────── */

const POLL_INTERVAL_MS = 2000;

export class SentryDebugController {
  private onChange: (() => void) | null = null;
  private pollHandle: number | null = null;

  init(onChange: () => void): void {
    this.onChange = onChange;
    // Trackers don't emit events, so poll. Match the deleted widget's 2s
    // cadence — it was empirically chosen to feel live without thrashing.
    this.pollHandle = window.setInterval(() => {
      this.onChange?.();
    }, POLL_INTERVAL_MS);
  }

  destroy(): void {
    if (this.pollHandle !== null) {
      window.clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
    this.onChange = null;
  }

  getState(): SentryDebugState {
    // Read each tracker behind its own try/catch — one bad read should
    // not blank the entire panel. Same defensive pattern the widget used.
    let formatted: Record<string, string | number | boolean | string[]> = {};
    try {
      formatted = eventTracker.getFormattedCounts();
    } catch {
      formatted = {};
    }

    let eventOrder: string[] = [];
    try {
      const fromOrderKey = formatted[INTERACTIONS_ORDER_KEY];
      if (typeof fromOrderKey === 'string' && fromOrderKey.length > 0) {
        eventOrder = fromOrderKey.split(',');
      } else {
        eventOrder = eventTracker.getEventOrder();
      }
    } catch {
      eventOrder = [];
    }

    let parsed: ParsedClickData = { navTabs: {}, jackpotStatsOpens: {}, gameLaunches: [], otherClicks: {}, total: 0 };
    try {
      parsed = parseClickData(formatted);
    } catch {
      parsed = { navTabs: {}, jackpotStatsOpens: {}, gameLaunches: [], otherClicks: {}, total: 0 };
    }

    let userData: Record<string, string | number> = {};
    try {
      userData = userDataTracker.getFormattedUserData();
    } catch {
      userData = {};
    }

    let transaction: Record<string, string | number> = {};
    try {
      transaction = transactionTracker.getFormattedTransactions();
    } catch {
      transaction = {};
    }

    let sectionNavigation: Record<string, string | number> = {};
    try {
      sectionNavigation = sectionNavigationTracker.getFormattedData();
    } catch {
      sectionNavigation = {};
    }

    let dynamicSections: Record<string, string | number> = {};
    try {
      dynamicSections = dynamicSectionsTracker.getFormattedData();
    } catch {
      dynamicSections = {};
    }

    let searchQueries: string[] = [];
    try {
      searchQueries = sessionService.getSearchQueries();
    } catch {
      searchQueries = [];
    }

    return {
      eventOrder,
      navTabs: parsed.navTabs,
      jackpotStatsOpens: parsed.jackpotStatsOpens,
      gameLaunches: parsed.gameLaunches,
      otherClicks: parsed.otherClicks,
      total: parsed.total,
      userData,
      transaction,
      sectionNavigation,
      dynamicSections,
      searchQueries,
      activeSpanTraceId: readActiveSpanTraceId(),
      lastUpdate: Date.now(),
    };
  }
}
